// src/components/admin/workflow/InspectionDetailsModal.jsx

import React, { useState, useEffect } from 'react';
import { adminInspectionService } from '../../../services/adminService';
import './InspectionDetailsModal.css';

const InspectionDetailsModal = ({ inspection, onClose }) => {
  // ✅ ALL hooks must be inside the component — not outside
  const [downloading, setDownloading] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [imageLoadErrors, setImageLoadErrors] = useState({});

  useEffect(() => {
    console.log('Inspection Data:', inspection);
    console.log('Images:', inspection.images);
  }, [inspection]);

  const getStatusColor = (status) => {
    const colors = {
      'ok': '#28a745',
      'not_ok': '#dc3545',
      'needs_attention': '#ffc107',
      'pending': '#ffc107',
      'scheduled': '#17a2b8',
      'completed': '#28a745',
      'priced': '#6f42c1',
      'confirmed': '#20c997',
      'cancelled': '#dc3545'
    };
    return colors[status] || '#6c757d';
  };

  const getScoreColor = (score) => {
    const numScore = parseFloat(score);
    if (numScore >= 8) return '#28a745';
    if (numScore >= 6) return '#ffc107';
    if (numScore >= 4) return '#ff9800';
    return '#dc3545';
  };

  // ✅ adminInspectionService is now imported above — no more "not defined" error
  const handleDownloadPdf = async () => {
    try {
      setDownloading(true);
      await adminInspectionService.downloadPdf(inspection.id);
    } catch (error) {
      alert('Failed to download PDF: ' + (error.response?.data?.message || error.message));
    } finally {
      setDownloading(false);
    }
  };

  const handleImageError = (imageId, url) => {
    console.error('Failed to load image:', url);
    setImageLoadErrors(prev => ({ ...prev, [imageId]: true }));
  };

  const handleImageLoad = (imageId) => {
    console.log('Image loaded successfully:', imageId);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-large inspection-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <h2>📋 Inspection Report - #{inspection.id}</h2>
            <p className="booking-ref">
              Booking Reference: <code>{inspection.booking_reference}</code>
              <span className="status-badge" style={{ 
                marginLeft: '12px',
                background: `${getStatusColor(inspection.status)}20`, 
                color: getStatusColor(inspection.status) 
              }}>
                {inspection.status}
              </span>
            </p>
          </div>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <button
              className="btn-download-pdf"
              onClick={handleDownloadPdf}
              disabled={downloading}
            >
              {downloading ? '⏳ Generating...' : '📄 Download PDF'}
            </button>
            <button className="modal-close" onClick={onClose}>✕</button>
          </div>
        </div>

        {/* Tabs */}
        <div className="modal-tabs">
          <button 
            className={activeTab === 'overview' ? 'active' : ''}
            onClick={() => setActiveTab('overview')}
          >
            Overview
          </button>
          <button 
            className={activeTab === 'details' ? 'active' : ''}
            onClick={() => setActiveTab('details')}
          >
            Inspection Details
          </button>
          <button 
            className={activeTab === 'images' ? 'active' : ''}
            onClick={() => setActiveTab('images')}
          >
            Images ({inspection.images?.length || 0})
          </button>
        </div>

        <div className="modal-body">

          {/* ── OVERVIEW TAB ── */}
          {activeTab === 'overview' && (
            <>
              <div className="report-section">
                <h3>🚗 Vehicle Information</h3>
                <div className="details-grid">
                  <div className="detail-item">
                    <label>Vehicle</label>
                    <p className="highlight-text">
                      {inspection.sell_request?.brand} {inspection.sell_request?.model}
                    </p>
                  </div>
                  <div className="detail-item">
                    <label>Year</label>
                    <p>{inspection.sell_request?.year}</p>
                  </div>
                  <div className="detail-item">
                    <label>KM Driven</label>
                    <p>{inspection.sell_request?.km_driven?.toLocaleString()} km</p>
                  </div>
                  <div className="detail-item">
                    <label>Inspection Type</label>
                    <p style={{ textTransform: 'capitalize' }}>{inspection.inspection_type || 'Standard'}</p>
                  </div>
                  <div className="detail-item">
                    <label>Location</label>
                    <p>📍 {inspection.city}</p>
                  </div>
                  <div className="detail-item">
                    <label>Inspection Date</label>
                    <p>
                      {inspection.preferred_date 
                        ? new Date(inspection.preferred_date).toLocaleDateString()
                        : 'Not scheduled'}
                    </p>
                  </div>
                </div>
              </div>

              <div className="info-grid">
                <div className="report-section">
                  <h3>👤 Seller Information</h3>
                  <div className="info-list">
                    <div className="info-item">
                      <label>Name</label>
                      <p>{inspection.seller?.name || inspection.contact_person || 'Not Provided'}</p>
                    </div>
                    <div className="info-item">
                      <label>Phone</label>
                      <p>{inspection.seller?.phone || inspection.contact_phone || 'Not Provided'}</p>
                    </div>
                    <div className="info-item">
                      <label>Email</label>
                      <p>{inspection.seller?.email || 'Not Provided'}</p>
                    </div>
                  </div>
                </div>

                {inspection.inspector && (
                  <div className="report-section">
                    <h3>🔍 Inspector Information</h3>
                    <div className="info-list">
                      <div className="info-item">
                        <label>Inspector Name</label>
                        <p>{inspection.inspector.name}</p>
                      </div>
                      <div className="info-item">
                        <label>Inspector ID</label>
                        <p>#{inspection.inspector.id}</p>
                      </div>
                      <div className="info-item">
                        <label>Email</label>
                        <p>{inspection.inspector.email}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {inspection.inspection_report && (
                <div className="report-section highlight-section">
                  <h3>📊 Overall Inspection Score</h3>
                  <div className="overall-score-container">
                    <div className="score-circle" style={{ 
                      borderColor: getScoreColor(inspection.inspection_report.overall_score) 
                    }}>
                      <span className="score-number" style={{ 
                        color: getScoreColor(inspection.inspection_report.overall_score) 
                      }}>
                        {inspection.inspection_report.overall_score}
                      </span>
                      <span className="score-denominator">/10</span>
                    </div>
                    <div className="critical-flags">
                      <div className={`flag-item ${!inspection.inspection_report.flooded ? 'success' : 'danger'}`}>
                        <span className="flag-icon">
                          {inspection.inspection_report.flooded ? '❌' : '✅'}
                        </span>
                        <div>
                          <strong>Flooded Vehicle</strong>
                          <p>{inspection.inspection_report.flooded ? 'Yes - Critical Issue' : 'No - Clear'}</p>
                        </div>
                      </div>
                      <div className={`flag-item ${!inspection.inspection_report.meter_tampered ? 'success' : 'danger'}`}>
                        <span className="flag-icon">
                          {inspection.inspection_report.meter_tampered ? '❌' : '✅'}
                        </span>
                        <div>
                          <strong>Odometer Tampered</strong>
                          <p>{inspection.inspection_report.meter_tampered ? 'Yes - Critical Issue' : 'No - Verified'}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {inspection.estimated_price && (
                <div className="report-section highlight-section">
                  <h3>💰 Pricing Information</h3>
                  <div className="details-grid">
                    <div className="detail-item">
                      <label>Estimated Price</label>
                      <p className="price-large">₹{inspection.estimated_price.toLocaleString()}</p>
                    </div>
                    {inspection.sell_request?.price_expectation && (
                      <div className="detail-item">
                        <label>Seller Expectation</label>
                        <p style={{ fontSize: '18px', fontWeight: '600' }}>
                          ₹{inspection.sell_request.price_expectation.toLocaleString()}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {inspection.payments && inspection.payments.length > 0 && (
                <div className="report-section">
                  <h3>💳 Payment Information</h3>
                  <div className="details-grid">
                    <div className="detail-item">
                      <label>Payment Status</label>
                      <p>
                        <span 
                          className="status-badge"
                          style={{ 
                            background: inspection.payments[0].status === 'success' ? '#28a74520' : '#ffc10720',
                            color: inspection.payments[0].status === 'success' ? '#28a745' : '#ffc107'
                          }}
                        >
                          {inspection.payments[0].status}
                        </span>
                      </p>
                    </div>
                    <div className="detail-item">
                      <label>Amount Paid</label>
                      <p>₹{(inspection.payments[0].amount_cents / 100).toFixed(2)}</p>
                    </div>
                    <div className="detail-item">
                      <label>Transaction ID</label>
                      <p><code>{inspection.payments[0].razorpay_payment_id || 'N/A'}</code></p>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {/* ── DETAILS TAB ── */}
          {activeTab === 'details' && (
            <>
              {inspection.inspection_report && (
                <>
                  <div className="report-section">
                    <h3>📈 Category Scores Breakdown</h3>
                    <div className="category-scores">
                      {inspection.inspection_report.core_systems_score && (
                        <div className="score-bar-item">
                          <div className="score-label">
                            <span>🔧 Core Systems</span>
                            <strong style={{ color: getScoreColor(inspection.inspection_report.core_systems_score) }}>
                              {inspection.inspection_report.core_systems_score}/10
                            </strong>
                          </div>
                          <div className="score-bar">
                            <div 
                              className="score-fill" 
                              style={{ 
                                width: `${(parseFloat(inspection.inspection_report.core_systems_score) / 10) * 100}%`,
                                backgroundColor: getScoreColor(inspection.inspection_report.core_systems_score)
                              }}
                            />
                          </div>
                        </div>
                      )}
                      {inspection.inspection_report.supporting_systems_score && (
                        <div className="score-bar-item">
                          <div className="score-label">
                            <span>⚙️ Supporting Systems</span>
                            <strong style={{ color: getScoreColor(inspection.inspection_report.supporting_systems_score) }}>
                              {inspection.inspection_report.supporting_systems_score}/10
                            </strong>
                          </div>
                          <div className="score-bar">
                            <div 
                              className="score-fill" 
                              style={{ 
                                width: `${(parseFloat(inspection.inspection_report.supporting_systems_score) / 10) * 100}%`,
                                backgroundColor: getScoreColor(inspection.inspection_report.supporting_systems_score)
                              }}
                            />
                          </div>
                        </div>
                      )}
                      {inspection.inspection_report.interiors_ac_score && (
                        <div className="score-bar-item">
                          <div className="score-label">
                            <span>❄️ Interiors & AC</span>
                            <strong style={{ color: getScoreColor(inspection.inspection_report.interiors_ac_score) }}>
                              {inspection.inspection_report.interiors_ac_score}/10
                            </strong>
                          </div>
                          <div className="score-bar">
                            <div 
                              className="score-fill" 
                              style={{ 
                                width: `${(parseFloat(inspection.inspection_report.interiors_ac_score) / 10) * 100}%`,
                                backgroundColor: getScoreColor(inspection.inspection_report.interiors_ac_score)
                              }}
                            />
                          </div>
                        </div>
                      )}
                      {inspection.inspection_report.exteriors_lights_score && (
                        <div className="score-bar-item">
                          <div className="score-label">
                            <span>🚘 Exteriors & Lights</span>
                            <strong style={{ color: getScoreColor(inspection.inspection_report.exteriors_lights_score) }}>
                              {inspection.inspection_report.exteriors_lights_score}/10
                            </strong>
                          </div>
                          <div className="score-bar">
                            <div 
                              className="score-fill" 
                              style={{ 
                                width: `${(parseFloat(inspection.inspection_report.exteriors_lights_score) / 10) * 100}%`,
                                backgroundColor: getScoreColor(inspection.inspection_report.exteriors_lights_score)
                              }}
                            />
                          </div>
                        </div>
                      )}
                      {inspection.inspection_report.wear_tear_score && (
                        <div className="score-bar-item">
                          <div className="score-label">
                            <span>🔨 Wear & Tear</span>
                            <strong style={{ color: getScoreColor(inspection.inspection_report.wear_tear_score) }}>
                              {inspection.inspection_report.wear_tear_score}/10
                            </strong>
                          </div>
                          <div className="score-bar">
                            <div 
                              className="score-fill" 
                              style={{ 
                                width: `${(parseFloat(inspection.inspection_report.wear_tear_score) / 10) * 100}%`,
                                backgroundColor: getScoreColor(inspection.inspection_report.wear_tear_score)
                              }}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {inspection.inspection_report.next_service_due && (
                    <div className="report-section">
                      <h3>🛠️ Service Information</h3>
                      <div className="details-grid">
                        <div className="detail-item">
                          <label>Next Service Due</label>
                          <p className="highlight-text">
                            {new Date(inspection.inspection_report.next_service_due).toLocaleDateString('en-IN', {
                              year: 'numeric', month: 'long', day: 'numeric'
                            })}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}

              {inspection.inspection_items && inspection.inspection_items.length > 0 && (
                <div className="report-section">
                  <h3>🔧 Detailed Inspection Items ({inspection.inspection_items.length})</h3>
                  <div className="inspection-items-list">
                    {inspection.inspection_items.map((item, index) => (
                      <div key={item.id || index} className="inspection-item-card">
                        <div className="item-header">
                          <div className="item-title">
                            <span className="status-icon" style={{ color: getStatusColor(item.status) }}>
                              {item.status === 'ok' ? '✅' : item.status === 'not_ok' ? '❌' : '⚠️'}
                            </span>
                            <div>
                              <h4>{item.category}</h4>
                              {item.name && <p className="item-name">{item.name}</p>}
                            </div>
                          </div>
                          <span 
                            className="status-badge"
                            style={{ 
                              background: `${getStatusColor(item.status)}20`, 
                              color: getStatusColor(item.status) 
                            }}
                          >
                            {item.status.replace('_', ' ').toUpperCase()}
                          </span>
                        </div>
                        {item.remarks && (
                          <div className="item-remarks">
                            <strong>Remarks:</strong> {item.remarks}
                          </div>
                        )}
                        <div className="item-footer">
                          <small>Checked on: {new Date(item.created_at).toLocaleDateString()}</small>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          {/* ── IMAGES TAB ── */}
          {activeTab === 'images' && (
            <div className="report-section">
              <h3>📸 Inspection Images ({inspection.images?.length || 0})</h3>
              {inspection.images && inspection.images.length > 0 ? (
                <div className="images-grid">
                  {inspection.images.map((img, index) => (
                    <div 
                      key={img.id || index} 
                      className="image-card"
                      onClick={() => !imageLoadErrors[img.id] && setSelectedImage(img.url)}
                    >
                      {!imageLoadErrors[img.id] ? (
                        <>
                          <img 
                            src={img.url} 
                            alt={`Inspection ${index + 1}`}
                            onLoad={() => handleImageLoad(img.id)}
                            onError={() => handleImageError(img.id, img.url)}
                            crossOrigin="anonymous"
                          />
                          <div className="image-overlay">
                            <span>🔍 Click to view full size</span>
                          </div>
                        </>
                      ) : (
                        <div className="image-error">
                          <div className="error-icon">📷</div>
                          <p>Image not available</p>
                          <small>{img.filename || 'Unknown file'}</small>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="no-images">
                  <div className="no-images-icon">📷</div>
                  <p>No inspection images available</p>
                  <small>Images will appear here once uploaded by the inspector</small>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="modal-footer">
          <button className="btn-modal-cancel" onClick={onClose}>
            Close Report
          </button>
          <button
            className="btn-download-pdf"
            onClick={handleDownloadPdf}
            disabled={downloading}
          >
            {downloading ? '⏳ Generating...' : '📄 Download PDF Report'}
          </button>
        </div>
      </div>

      {/* Image Lightbox */}
      {selectedImage && (
        <div className="image-lightbox" onClick={() => setSelectedImage(null)}>
          <button className="lightbox-close" onClick={() => setSelectedImage(null)}>✕</button>
          <img 
            src={selectedImage} 
            alt="Full size inspection"
            onError={(e) => {
              console.error('Lightbox image failed to load');
              e.target.style.display = 'none';
            }}
          />
        </div>
      )}
    </div>
  );
};

export default InspectionDetailsModal;